import React, { useMemo } from "react";
import { getLocale, useIntl } from "umi";
import {
  MetricsItemType,
  MetricsObjType,
  metricRenderType,
  metricRenderItemType,
  ISeriesItem,
  ITableOption,
  IMultiMetricConfig,
} from "./_type";
import { useLocal, useSelectorMap, useWeekDay } from "@/pages/dashboard/utils/customHook";
import { formatNum, getSystemColor } from "@/pages/dashboard/utils/utils";
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
import moment from "moment";
import { dynamicString } from '@home/utils/dynamicString';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import {
  resolveAndSelectName,
  resolveHeaderName,
  prepareMetricsDataSource,
  TMetricsInfo,
  IMetricsDataSource,
  ICustomName,
} from '@/pages/dashboard/utils/metricUtils';
import { getLanguage, getName } from "@home/utils/common";

/**
 * 组件配置类型
 */
interface IComponentConfig {
  /** 指标类型 */
  metricType?: string;
  /** 时间类型 */
  timeType?: string;
  /** 指标 key */
  metricKey?: string;
  /** 指标总量 key */
  metricTotalKey?: string;
  /** 累计 key */
  accumateKey?: string;
  /** 自定义名称映射 */
  customNameMap?: Record<string, string>;
  /** 自定义 AU 图例 */
  customeAuLegend?: string[];
  /** 自定义表格名称映射 */
  customTableNameMap?: Record<string, string>;
  /** AU 名称配置 */
  auName?: ICustomName;
  /** 系统名称配置 */
  systemName?: ICustomName;
  /** AU 表格宽度 */
  auTableWidth?: number;
  /** 系统表格宽度 */
  systemTableWidth?: number;
  /** 英文指标名称 */
  metricEnName?: string;
  /** 中文指标名称 */
  metricCnName?: string;
  /** 格式化规则 */
  formatRule?: string;
  /** 指标信息 */
  metricsInfo?: TMetricsInfo;
  /** 多指标配置 */
  multiMetrics?: IMultiMetricConfig[];
  /** 自定义求和名称 */
  customSumName?: ICustomName;
  /** 是否显示求和 */
  showSum?: boolean;
}

/**
 * 数据项类型
 */
interface IDataItem {
  /** 日期 */
  date: string;
  /** 指标数据 */
  metrics?: Record<string, { value?: string | number; }>;
  /** 其他字段 */
  [key: string]: unknown;
}

/**
 * 表格记录类型
 */
type TTableRecord = Record<string, string | number>;

/**
 * 分解数据项类型
 */
interface IBreakdownItem {
  /** 日期 */
  date: string;
  /** 总量 */
  total?: string | number;
  /** 新用户 */
  new_users?: string;
  /** 留存用户 */
  retention_users?: string;
  /** 回流用户 */
  return_users?: string;
  /** 其他字段 */
  [key: string]: string | number | undefined;
}

/**
 * 渠道系统数据类型
 */
interface IChannelSystemData {
  /** 日期列表 */
  dates?: string[];
  /** 总量列表 */
  total?: (string | number)[];
  /** 指标列表 */
  metrics?: Array<{
    name: string;
    values: string[];
  }>;
}

export const useConstant = (type: string) => {
  const { formatMessage } = useIntl(); // 中英切换方法
  // 指标常量
  const metiricsMsg: MetricsObjType = useMemo(() => {
    return {
      DAY: {
        au: formatMessage({ id: 'overview_daily_DAU' }),
        title: {
          dau: formatMessage({ id: 'overview_daily_DAU_Breakdown' }),
          system: formatMessage({ id: 'overview_System_of_New_Users' })
        },
        breakdown_total: 'dau_breakdown_total',
        breakdown: 'dau_breakdown',
        new: formatMessage({ id: 'overview_monthly_New_Users' }),
        channel: 'channels_of_new_user'
      },
      WEEK: {
        au: formatMessage({ id: 'overview_weekly_WAU' }),
        title: {
          wau: formatMessage({ id: 'overview_weekly_WAU_Breakdown' }),
          system: formatMessage({ id: 'overview_System_of_New_Users' })
        },

        breakdown_total: 'wau_breakdown_total',
        breakdown: 'wau_breakdown',
        new: formatMessage({ id: 'overview_monthly_New_Users' }),
      },
      MONTH: {
        au: formatMessage({ id: 'overview_monthly_MAU' }),
        title: {
          mau: formatMessage({ id: 'overview_monthly_MAU_Breakdown' }),
          system: formatMessage({ id: 'overview_System_of_New_Users' })
        },
        breakdown_total: 'mau_breakdown_total',
        breakdown: 'mau_breakdown',
        new: formatMessage({ id: 'overview_monthly_New_Users' }),
      },
    };
  }, [getLocale()]);
  return metiricsMsg[type];
};

// 日期格式化
export const dateFormat = (type = 'DAY', value: string, isTable = false): string => {
  if (!value) return '';
  // MONTH维度时，x轴和表格显示为YYYY-MM
  // Day和Week维度时, x轴显示为 MM-DD, 表格显示为YYYY-MM-DD
  let formatStr = 'MM-DD';
  if (type === 'MONTH') {
    formatStr = 'YYYY-MM';
  } else if (isTable) {
    formatStr = 'YYYY-MM-DD';
  }
  return moment(value).format(formatStr);
};

/**
 * 求和值计算项类型
 */
interface ISumValueItem {
  /** 数值 */
  value?: string | number;
}

/**
 * 获取求和值
 * @param arr - 数据数组
 * @param isBar - 是否为柱状图
 * @returns 格式化后的求和值
 */
export const getSumValue = (arr: ISumValueItem[] = [], isBar: boolean): string => {
  const arrObject = isBar ? arr?.slice(0, arr.length - 1) : arr;
  const sumValue = arrObject?.reduce((acc, cur) => {
    if (!isNaN(parseFloat(String(cur.value)))) {
      return acc + parseFloat(String(cur.value));
    }
    return acc;
  }, 0);
  const formattedSum = sumValue ? String(formatNum(sumValue, false, true)) : '';
  return formattedSum || '-';
};

/**
 * 组件状态 Hook
 * @param data - 数据
 * @param metiricsMsg - 指标消息
 * @param config - 配置
 * @param isEn - 是否英文
 * @returns 渲染数据
 */
export const useComponontState = (
  data: IDataItem[] | Record<string, unknown> | null,
  metiricsMsg: MetricsItemType,
  config: IComponentConfig,
  isEn: boolean
): metricRenderItemType | undefined => {
  const { metricType: metric, timeType: type, metricKey: configMetricKey, metricTotalKey, accumateKey = 'os',
    customNameMap, customeAuLegend, customTableNameMap, auName, systemName, 
    auTableWidth = 80, systemTableWidth = 80, metricEnName, metricCnName, formatRule, metricsInfo, multiMetrics,
    customSumName, showSum, noStack } = config || {};
  const { getWeekDay } = useWeekDay();
  const { locale, getLocalText } = useLocal('overview', '_');
  const { getSelectorText } = useSelectorMap();
  const isOs = accumateKey === 'os';
  const metricsDataSource: IMetricsDataSource = useMemo(() => prepareMetricsDataSource(metricsInfo), [metricsInfo]);
  
  // 判断是否为多指标模式
  const isMultiMetricsMode = multiMetrics && Array.isArray(multiMetrics) && multiMetrics.length > 0;

  // 解析 formatRule(如果存在),需要在 useMemo 外部定义
  const resolvedFormatRule = useMemo<string | null>(() => {
    if (formatRule && metricsInfo) {
      return dynamicString(formatRule, metricsDataSource, '');
    }
    return null;
  }, [formatRule, metricsDataSource, metricsInfo]);

  /**
   * 判断数据是否都是极小值（小于1）
   * @param values - 数值数组
   * @returns 是否都是极小值
   */
  const isAllSmallValues = (values: (number | string)[]): boolean => {
    const numericValues = values
      .filter(val => val !== '' && val !== null && val !== undefined)
      .map(val => parseFloat(String(val)))
      .filter(val => !isNaN(val));
    
    if (numericValues.length === 0) return false;
    
    // 如果所有非零值都小于1，则认为是极小值
    return numericValues.every(val => val === 0 || Math.abs(val) < 1);
  };

  const compRenderObj = useMemo((): metricRenderType => {
    // 当前 metric 的 key，确保为字符串
    const currentMetricKey = metric || '';
    
    if (data === null || (Array.isArray(data) && data.length === 0)) {
      const emptyResult: metricRenderItemType = {
        isNoData: true,
        legendData: [],
        xAxisData: [],
        barSeries: [],
        lineSeries: [],
        areaSeries: [],
        tableList: [],
        tableOption: [],
      };
      // 多指标模式返回 multiMetrics 结构
      if (isMultiMetricsMode) {
        return { multiMetrics: emptyResult };
      }
      return { [currentMetricKey]: emptyResult };
    }

    // 多指标模式处理逻辑
    if (isMultiMetricsMode) {
      const dates: string[] = [];
      const metricsMap: Record<string, (number | string)[]> = {};
      
      // 解析数据结构 - 支持你提供的数据格式
      (data as IDataItem[]).forEach((item: IDataItem) => {
        dates.push(item.date);
        multiMetrics.forEach((metricConfig: IMultiMetricConfig) => {
          const metricKeyName = metricConfig.key;
          if (!metricsMap[metricKeyName]) {
            metricsMap[metricKeyName] = [];
          }
          const metricValue = item.metrics?.[metricKeyName]?.value ?? (item as Record<string, unknown>)[metricKeyName];
          metricsMap[metricKeyName].push((metricValue ?? '') as string | number);
        });
      });

      // 构建 legend 数据
      const legendData = multiMetrics.map((metricConfig: IMultiMetricConfig) => {
        const { key, enName, cnName } = metricConfig;
        
        const metricInfoName = metricsDataSource?.metricsInfo?.[key];
        
        const resolvedEn = enName ? dynamicString(enName, metricsDataSource, '') : undefined;
        const resolvedCn = cnName ? dynamicString(cnName, metricsDataSource, '') : undefined;
        
        const finalEn = resolvedEn || metricInfoName?.en || key;
        const finalCn = resolvedCn || metricInfoName?.cn || key;

        return getName(isEn, finalEn, finalCn);
      });

      // 构建柱状图 series
      const barSeries: ISeriesItem[] = multiMetrics.map((metricConfig: IMultiMetricConfig, index: number) => {
        const { key, yAxisIndex, chartType } = metricConfig;
        const name = legendData[index];
        const isLine = chartType === 'line';
        return {
          name,
          type: (isLine ? 'line' : 'bar') as 'bar' | 'line',
          stack: isLine ? undefined : (noStack ? undefined : 'Total'),
          barMaxWidth: 80,
          barCategoryGap: '50%',
          cursor: 'pointer',
          yAxisIndex: yAxisIndex || 0,
          data: metricsMap[key] || [],
        };
      });

      // 构建折线图 series
      const lineSeries: ISeriesItem[] = multiMetrics.map((metricConfig: IMultiMetricConfig, index: number) => {
        const { key, yAxisIndex } = metricConfig;
        const name = legendData[index];
        return {
          name,
          type: 'line' as const,
          cursor: 'pointer',
          yAxisIndex: yAxisIndex || 0,
          data: metricsMap[key] || [],
        };
      });

      // 构建折线面积图 series (堆积)
      const areaSeries: ISeriesItem[] = multiMetrics.map((metricConfig: IMultiMetricConfig, index: number) => {
        const { key, yAxisIndex } = metricConfig;
        const name = legendData[index];
        return {
          name,
          type: 'line' as const,
          cursor: 'pointer',
          stack: 'Total',
          yAxisIndex: yAxisIndex || 0,
          areaStyle: {
            opacity: 0.2
          },
          data: metricsMap[key] || [],
        };
      });

      // 构建表格数据
      const tableList = dates.map((date, index) => {
        const row: Record<string, string | number> = { date };
        let rowSum = 0;
        let hasValue = false;
        multiMetrics.forEach((metricConfig: IMultiMetricConfig) => {
          const { key } = metricConfig;
          const val = metricsMap[key]?.[index];
          row[key] = val ?? '';
          if (showSum && val !== '' && val !== undefined) {
            rowSum += Number(val);
            hasValue = true;
          }
        });
        if (showSum) {
          row.sum = hasValue ? rowSum : '';
        }
        return row;
      });

      // 构建表格配置
      const tableOption: ITableOption[] = [
        {
          key: "date",
          align: "left",
          fixed: "left",
          width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: getLocalText('table_date'),
          render: (cvm: Record<string, string | number>) => {
            const weekDay = getWeekDay(String(cvm?.date));
            const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
            return <>
              <span className='table-text'>{dateFormat(type, String(cvm?.date), true)}</span>
              {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
            </>;
          },
        },
      ];

      // 添加求和列
      if (showSum) {
        const sumHeader = resolveAndSelectName(
          isEn, 
          customSumName?.enName, 
          customSumName?.cnName, 
          metricsInfo, 
          undefined, 
          isEn ? 'Total' : '总计'
        );
        tableOption.push({
          key: 'sum',
          align: 'right',
          width: 100 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: sumHeader,
          render: (cvm: Record<string, string | number>) => <span>{formatNum(cvm.sum, false, true)}</span>
        });
      }

      // 为每个指标添加表格列
      multiMetrics.forEach((metricConfig: IMultiMetricConfig, index: number) => {
        const { key, formatter, columnWidthEn, columnWidthCn } = metricConfig;
        const columnName = legendData[index];

        // 根据当前语言选择对应宽度
        const resolvedColumnWidth = getLanguage(isEn, columnWidthEn, columnWidthCn) || 100;
        
        // 解析 formatter
        let resolvedFormatter = formatter;
        if (formatter && metricsInfo) {
          resolvedFormatter = dynamicString(formatter, metricsDataSource, '');
        }
        
        // 如果没有配置 formatter,尝试从 metricsInfo 获取
        if (!resolvedFormatter) {
          resolvedFormatter = metricsDataSource?.metricsInfo?.[key]?.formatter;
        }

        tableOption.push({
          key,
          align: "right",
          width: resolvedColumnWidth + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: columnName,
          formatter: resolvedFormatter,
          render: (cvm: Record<string, string | number>) => {
            const value = cvm[key];
            if (resolvedFormatter) {
              return <span>{dataFormat({ value, formatRule: resolvedFormatter })}</span>;
            }
            return <span>{formatNum(value, false, true)}</span>;
          },
        });
      });

      return {
        multiMetrics: {
          isNoData: dates.length === 0,
          legendData,
          xAxisData: dates,
          barSeries,
          lineSeries,
          areaSeries,
          tableList,
          tableOption,
          yAxisConfig: multiMetrics.map((metricConfig: IMultiMetricConfig) => {
            let finalYAxisFormatter = metricConfig.yAxisFormatter || metricConfig.formatter;
            
            // 如果 yAxisFormatter 是 bmk，检查对应指标的数据是否都是极小值
            if (finalYAxisFormatter === 'bmk') {
              const metricValues = metricsMap[metricConfig.key] || [];
              if (isAllSmallValues(metricValues)) {
                finalYAxisFormatter = 'thousands2Decimal';
              }
            }
            
            return {
              yAxisIndex: metricConfig.yAxisIndex ?? 0,
              formatter: finalYAxisFormatter,
            };
          }),
        }
      };
    }

    // 原有的单指标逻辑保持不变
    const dataRecord = data as Record<string, unknown>;
    const channelSystemData = (configMetricKey ? dataRecord?.[configMetricKey] : dataRecord?.system_of_new_user) as IChannelSystemData | undefined;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    const channelsNewUserV2Data = channelSystemData?.metrics?.filter((item) => {
      return item.values?.filter((items: string) => items !== '')?.length > 0;
    }) || [];
    const channelsNewUserV2Name = channelsNewUserV2Data?.map((items) => items?.name) || [];

    const nameMap: Record<string, string> = customNameMap || {
      [getLocalText('breakdown_legend1')]: 'new_users',
      [getLocalText('breakdown_legend2')]: 'retention_users',
      [getLocalText('breakdown_legend3')]: 'return_users',
    };
    // DAU/WAU/MAU
    const breakdownData = dataRecord?.[metiricsMsg?.breakdown] as IBreakdownItem[] | undefined;
    const isHaveAuData = Object.keys(breakdownData?.[0] || {})?.length > 0;
    let auLegendData: string[] = [];
    if (isHaveAuData) {
      auLegendData = customeAuLegend || [
        getLocalText('breakdown_legend1'),
        getLocalText('breakdown_legend2'),
        getLocalText('breakdown_legend3'),
      ];
    }
    const systemLegendData = channelsNewUserV2Name?.map((item: string) => {
      return getSelectorText(accumateKey, item);
    });

    const auBarSeries: ISeriesItem[] = auLegendData.reduce((org: ISeriesItem[], t) => {
      org.push({
        name: t,
        type: 'bar' as const,
        stack: "Total",
        barMaxWidth: 80,
        barCategoryGap: '50%',
        cursor: 'pointer',
        data: breakdownData?.reduce((o: (number | string)[], v: IBreakdownItem) => {
          const name = nameMap[t];
          const value = v[name?.toLowerCase()?.replace(' ', '_')];
          o.push(value ?? '');
          return o;
        }, []) || [],
      });
      return org;
    }, []) || [];

    const systemBarSeries: ISeriesItem[] = channelsNewUserV2Name?.map((name: string, index: number) => {
      const metricValue = channelsNewUserV2Data[index] || { name: '', values: [] };
      const rawSeriesData: string[] = metricValue?.name === name ? metricValue.values : [];
      const seriesData: (number | string)[] = rawSeriesData.map((item: string) => item ?? '');
      const osName = getSelectorText(accumateKey, name).toLowerCase().replace(/\s/g, '');
      const color = metric === 'system' ? getSystemColor(osName) : '';

      return {
        name: getSelectorText(accumateKey, name),
        type: 'bar' as const,
        stack: "Total",
        barMaxWidth: 80,
        barCategoryGap: '50%',
        cursor: 'pointer',
        data: seriesData,
        ...(isOs ? {
          itemStyle: {
            color
          },
        } : {})
      };
    }) || [];

    // 折线图series配置
    // 第一个卡片
    const aulineSeries: ISeriesItem[] = auLegendData.reduce((org: ISeriesItem[], t) => {
      org.push({
        name: t,
        type: 'line' as const,
        barMaxWidth: 80,
        cursor: 'pointer',
        data: breakdownData?.reduce((o: (number | string)[], v: IBreakdownItem) => {
          const name = nameMap[t];
          const value = v[name?.toLowerCase()?.replace(' ', '_')];
          o.push(value ?? '');
          return o;
        }, []) || [],
      });
      return org;

    }, []) || [];

    // 第二个卡片
    const systemLineSeries: ISeriesItem[] = channelsNewUserV2Name?.map((name: string, index: number) => {
      const metricValue = channelsNewUserV2Data[index] || { name: '', values: [] };
      const lineData = metricValue?.name === name ? metricValue.values : [];
      const osName = getSelectorText(accumateKey, name).toLowerCase().replace(/\s/g, '');
      const color = metric === 'system' ? getSystemColor(osName) : '';

      // Customize the properties to match lineSeries1
      return {
        name: getSelectorText(accumateKey, name),
        type: 'line' as const,
        data: lineData.map((value: string) => value ?? ''),
        ...(isOs ? {
          itemStyle: {
            color
          },
        } : {})
      };
    }) || [];

    const auTableList: Record<string, string | number>[] = breakdownData?.reduce((org: Record<string, string | number>[], t: IBreakdownItem) => {
      org.push(Object.assign({ [metiricsMsg?.au]: t?.total ?? '' }, t) as Record<string, string | number>);
      return org;
    }, []) || [];
    const { dates = [], total = [] } = channelSystemData || {};
    const systemTableList: Record<string, string | number>[] = dates?.map((date: string, index: number) => {
      return {
        date,
        'New Users': total[index] ?? '',
        ...channelsNewUserV2Data.reduce((acc: Record<string, string | number>, metricItem) => {
          acc[getSelectorText(accumateKey, metricItem?.name)] = metricItem?.values?.[index] ?? '';
          return acc;
        }, {})
      };
    }) || [];

    // 扩展维度名称自定义
    // 优先级:metricEnName/metricCnName > auName > 默认值
    const auHeader = resolveHeaderName(
      isEn,
      metiricsMsg?.au,
      metricEnName,
      metricCnName,
      auName,
      metricsInfo
    );

    const auTableOption: ITableOption[] = [
      {
        key: "date",
        align: "left",
        fixed: "left",
        width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: getLocalText('table_date'),
        render: (cvm: TTableRecord) => {
          const weekDay = getWeekDay(String(cvm?.date));
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{dateFormat(type, String(cvm?.date), true)}</span>
            {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
      {
        key: metiricsMsg?.au,
        align: "right",
        fixed: "left",
        width: auTableWidth + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: auHeader,
        render: (cvm: TTableRecord) => {
          // 优先使用 formatRule,如果没有则使用原有的 formatNum
          if (resolvedFormatRule) {
            return <span>{dataFormat({ value: cvm[metiricsMsg?.au], formatRule: resolvedFormatRule })}</span>;
          }
          return <span>{formatNum(cvm[metiricsMsg?.au], false, true)}</span>;
        },
      },
    ];
    // 第一个卡片表格变化列
    const keyNameMap: Record<string, string> = customTableNameMap || {
      new_users: resolveAndSelectName(isEn, "{{metricsInfo.new_users_users.en}}", "{{metricsInfo.new_users_users.cn}}", metricsInfo, undefined, getLocalText('breakdown_legend1')),
      retention_users: resolveAndSelectName(isEn, "{{metricsInfo.retention_users.en}}", "{{metricsInfo.retention_users.cn}}", metricsInfo, undefined, getLocalText('breakdown_legend2')),
      return_users: resolveAndSelectName(isEn, "{{metricsInfo.return_users_users.en}}", "{{metricsInfo.return_users_users.cn}}", metricsInfo, undefined, getLocalText('breakdown_legend3')),
    };

    Object.keys(auTableList[0] || []).forEach((t) => {
      if (t === 'date' || t === 'total' || t === metiricsMsg?.au) return;
      auTableOption.push({
        key: t,
        align: "right",
        width: auTableWidth + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: keyNameMap[t],
        render: (cvm: { [x: string]: string | number; }) => {
          // 优先使用 formatRule,如果没有则使用原有的 formatNum
          const formatter = dynamicString(`{{metricsInfo.${t}.formatter}}`, metricsDataSource, '');
          if (formatter) {
            return <span>{dataFormat({ value: cvm[t], formatRule: formatter  })}</span>;
          }
          return <span>{formatNum(cvm[t], false, true)}</span>;
        },
      });
    });

    // 扩展维度名称自定义
    const systemHeader = resolveHeaderName(
      isEn,
      getLocalText('table_new_users'),
      metricEnName,
      metricCnName,
      systemName,
      metricsInfo
    );

    // 第二个卡片表格固定列
    const systemTableOption: ITableOption[] = [
      {
        key: "date",
        align: "left",
        fixed: "left",
        width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: getLocalText('table_date'),
        render: (cvm: TTableRecord) => {
          const weekDay = getWeekDay(String(cvm?.date));
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{dateFormat(type, String(cvm?.date), true)}</span>
            {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
      {
        key: "New Users",
        align: "right",
        fixed: "left",
        width: systemTableWidth + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: systemHeader,
        render: (cvm: TTableRecord) => {
          // const formatter = dynamicString(formatRule, metricsDataSource, '');
          if (resolvedFormatRule) {
            return <span>{dataFormat({ value: cvm['New Users'], formatRule: resolvedFormatRule  })}</span>;
          }
          return <span>{formatNum(cvm['New Users'], false, true)}</span>;
        },
      },
    ];
    // 第二个卡片表格变化列
    Object.keys(systemTableList[0] || []).forEach((t) => {
      if (t === 'date' || t === 'New Users') return;
      systemTableOption.push({
        key: t,
        align: "right",
        width: systemTableWidth + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: getSelectorText(accumateKey, t),
        render: (cvm: { [x: string]: string | number; }) => (<span>{formatNum(cvm[t], false, true)}</span>),
      });
    });

    const dauMetricsArr = ['dau', 'wau', 'mau'];

    if (dauMetricsArr?.includes(currentMetricKey)) {
      return {
        [currentMetricKey]: {
          isNoData: !dataRecord?.[metiricsMsg.breakdown_total],
          legendData: auLegendData,
          xAxisData: breakdownData?.reduce((org: string[], t: IBreakdownItem) => {
            org.push(t?.date);
            return org;
          }, []) || [],
          barSeries: auBarSeries,
          lineSeries: aulineSeries,
          areaSeries: aulineSeries.map((series: ISeriesItem) => ({
            ...series,
            stack: 'Total',
            areaStyle: {
              opacity: 0.2
            },
          })),
          tableList: auTableList,
          tableOption: auTableOption,
        }
      };
    } 
    return {
      [currentMetricKey]: {
        isNoData: metricTotalKey ? !dataRecord?.[metricTotalKey] : (!dataRecord?.system_of_new_user_total),
        legendData: systemLegendData,
        xAxisData: channelSystemData?.dates || [],
        barSeries: systemBarSeries,
        lineSeries: systemLineSeries,
        areaSeries: systemLineSeries.map((series: ISeriesItem) => ({
          ...series,
          stack: 'Total',
          areaStyle: {},
        })),
        tableList: systemTableList,
        tableOption: systemTableOption,
      },
    };
    
  }, [data, locale, metric, metricsInfo, isEn, metiricsMsg, auName, metricEnName, metricCnName, resolvedFormatRule, isMultiMetricsMode, multiMetrics, customSumName, showSum]);
  
  // 如果是多指标模式,返回 multiMetrics 的数据
  if (isMultiMetricsMode) {
    return compRenderObj?.multiMetrics;
  }
  
  return compRenderObj?.[metric || ''];
};